/**
 * Statement 增强类型定义
 * 用于支持多文件代码分离
 */

/**
 * 代码分类标识
 * - 'service': API 调用函数（如 fetchApi, batchSaveApi）
 * - 'hook': Hook 调用（如 usePageSearch, useRowSelection）
 * - 'state': 状态管理（如 useState, zustand store）
 * - 'store': 状态存储定义（如 zustand store 定义）
 * - 'constant': 常量定义（如 namespace, defaultGridRecord）
 * - 'util': 工具函数（如 getDataPath, formatDate）
 * - 'effect': 副作用（如 useEffect, useAsyncSearchEffect）
 * - 'callback': 回调函数（如 onCellEditChange）
 * - 'component': 组件逻辑（默认）
 * - 'schema': Schema 定义（如表单 schema、表格列定义）
 */
export type StatementCategory =
  | 'service'
  | 'hook'
  | 'state'
  | 'store'
  | 'constant'
  | 'util'
  | 'effect'
  | 'callback'
  | 'component'
  | 'schema';

/**
 * 已知的固定目标文件类型
 */
export type KnownTargetFile =
  | 'services.ts'
  | 'store.ts'
  | 'hooks.ts'
  | 'constants.ts'
  | 'utils.ts'
  | 'types.ts'
  | 'schema.tsx'
  | 'index.tsx';

/**
 * 目标文件类型
 * - 可以是固定的 KnownTargetFile（如 'hooks.ts'）
 * - 也可以是自由路径字符串（如 'hooks/useFormValueChange.ts'）
 */
export type TargetFile = KnownTargetFile | (string & {});

/**
 * 依赖项定义
 */
export interface StatementDependency {
  package: string;
  importName: string;
  default?: boolean;
  namespace?: boolean;
}

/**
 * 服务层元数据（仅当 category='service' 时有效）
 */
export interface ServiceLayerMeta {
  apiName: string;
  apiEndpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  requestType?: string;
  responseType?: string;
  description?: string;
}

/**
 * 增强的 Statement 接口
 * 在原有 Statement 基础上增加了分类和文件归属信息
 */
export interface EnhancedStatement {
  // 原有字段
  type: string;
  value: string | string[];
  names: string[];
  dependencies: StatementDependency[];
  source: string;
  id: string;
  metaInfos: Record<string, unknown>;
  references: string[];
  private?: boolean;

  /**
   * 是否可被用户访问使用
   * 为 true 时表示该变量可以暴露给用户使用(如在变量选择器中可见)
   * 为 false 或 undefined 时表示仅供内部使用
   */
  accessible?: boolean;

  // 增强字段(均可选,有默认值)

  /** 代码分类标识,默认 'component' */
  category?: StatementCategory;

  /** 目标文件,默认 'index.tsx' */
  targetFile?: TargetFile;

  /** 是否需要从目标文件导出,默认 false */
  exported?: boolean;

  /** 内部引用关系,该 statement 引用了哪些其他 statement(通过 id 列表) */
  internalRefs?: string[];

  /** 服务层元数据 */
  serviceLayer?: ServiceLayerMeta;
}

/**
 * 已知目标文件到 subModule 名称的映射
 * 用于设置 chunk 的 subModule 属性
 */
export const TARGET_FILE_TO_SUBMODULE: Record<KnownTargetFile, string> = {
  'services.ts': 'services',
  'store.ts': 'store',
  'hooks.ts': 'hooks',
  'constants.ts': 'constants',
  'utils.ts': 'utils',
  'types.ts': 'types',
  'schema.tsx': 'schema',
  'index.tsx': 'index',
};

/**
 * 已知目标文件到文件类型的映射
 * 用于设置 chunk 的 fileType 属性
 */
export const TARGET_FILE_TO_FILETYPE: Record<KnownTargetFile, string> = {
  'services.ts': 'ts',
  'store.ts': 'ts',
  'hooks.ts': 'ts',
  'constants.ts': 'ts',
  'utils.ts': 'ts',
  'types.ts': 'ts',
  'schema.tsx': 'tsx',
  'index.tsx': 'tsx',
};

/**
 * 从目标文件路径推断 subModule 名称
 * - 'services.ts'                → 'services'
 * - 'hooks/useFormValueChange.ts' → 'hooks/useFormValueChange'
 * - 'utils/formatDate.ts'         → 'utils/formatDate'
 *
 * @param targetFile - 目标文件路径
 * @returns subModule 名称
 */
export function getSubModuleFromTargetFile(targetFile: TargetFile): string {
  // 已知固定文件，使用映射表
  if (targetFile in TARGET_FILE_TO_SUBMODULE) {
    return TARGET_FILE_TO_SUBMODULE[targetFile as KnownTargetFile];
  }
  // 动态路径：去掉扩展名作为 subModule
  // 'hooks/useFormValueChange.ts' → 'hooks/useFormValueChange'
  return targetFile.replace(/\.(tsx?|jsx?)$/, '');
}

/**
 * 从目标文件路径推断 fileType
 * - '.tsx' → 'tsx'
 * - '.ts'  → 'ts'
 * - '.jsx' → 'jsx'
 * - '.js'  → 'js'
 *
 * @param targetFile - 目标文件路径
 * @returns 文件类型字符串
 */
export function getFileTypeFromTargetFile(targetFile: TargetFile): string {
  // 已知固定文件，使用映射表
  if (targetFile in TARGET_FILE_TO_FILETYPE) {
    return TARGET_FILE_TO_FILETYPE[targetFile as KnownTargetFile];
  }
  // 动态路径：根据扩展名推断
  const match = targetFile.match(/\.(tsx|jsx|ts|js)$/);
  return match ? match[1] : 'ts';
}

/**
 * 从目标文件路径推断导入路径
 * 用于生成内部导入语句中的 from 路径
 * - 'services.ts'                → './services'
 * - 'hooks/useFormValueChange.ts' → './hooks/useFormValueChange'
 *
 * @param targetFile - 目标文件路径
 * @returns 相对导入路径（不含扩展名）
 */
export function getImportPathFromTargetFile(targetFile: TargetFile): string {
  const subModule = getSubModuleFromTargetFile(targetFile);
  return `./${subModule}`;
}

/**
 * 默认目标文件
 */
export const DEFAULT_TARGET_FILE: TargetFile = 'index.tsx';

/**
 * 默认分类
 */
export const DEFAULT_CATEGORY: StatementCategory = 'component';
